import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/components/ui/use-toast';
import {
  CUSTOMER_PORTAL_OPERATIONS,
  useCustomerPortalSettings,
  useUpdateCustomerPortalSettings,
  type CustomerPortalOperation,
  type CustomerPortalSettings,
} from '@/lib/api/customerPortal';

import { PortalMountCard } from './PortalMountCard';

/** Only an explicit `false` turns an operation off, so an unconfigured store offers all of them. */
const isEnabled = (settings: CustomerPortalSettings | undefined, operation: CustomerPortalOperation) =>
  settings?.[operation] !== false;

const readAll = (settings: CustomerPortalSettings | undefined): Record<CustomerPortalOperation, boolean> =>
  Object.fromEntries(
    CUSTOMER_PORTAL_OPERATIONS.map((operation) => [operation, isEnabled(settings, operation)]),
  ) as Record<CustomerPortalOperation, boolean>;

const PortalOperationsCard = ({ storeId }: { storeId: string }) => {
  const { t } = useTranslation('stores');
  const { toast } = useToast();

  const { data: settings, isLoading } = useCustomerPortalSettings(storeId);
  const updateSettings = useUpdateCustomerPortalSettings();

  // `null` until the merchant touches a switch, so the card follows the server value while it
  // loads and again after a save, with no effect to keep the two in step.
  const [draft, setDraft] = useState<Record<CustomerPortalOperation, boolean> | null>(null);

  const saved = readAll(settings);
  const current = draft ?? saved;
  const hasChanges = CUSTOMER_PORTAL_OPERATIONS.some(
    (operation) => current[operation] !== saved[operation],
  );

  const handleSave = async () => {
    try {
      await updateSettings.mutateAsync({ storeId, settings: current });
      setDraft(null);
      toast({
        title: t('customerPortal.operations.successTitle'),
        description: t('customerPortal.operations.successDescription'),
      });
    } catch (error) {
      toast({
        title: t('customerPortal.operations.errorTitle'),
        description:
          error instanceof Error ? error.message : t('customerPortal.operations.errorDescription'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div>
          <CardTitle>{t('customerPortal.operations.title')}</CardTitle>
          <CardDescription>{t('customerPortal.operations.description')}</CardDescription>
        </div>
        <Button
          onClick={handleSave}
          disabled={!hasChanges || updateSettings.isPending || isLoading}
          className="flex-shrink-0 gap-2"
        >
          <Save className="h-4 w-4" />
          {updateSettings.isPending
            ? t('customerPortal.operations.saving')
            : t('customerPortal.operations.save')}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="space-y-2">
            {CUSTOMER_PORTAL_OPERATIONS.map((operation) => (
              <Skeleton key={operation} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          CUSTOMER_PORTAL_OPERATIONS.map((operation) => (
            <div key={operation} className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-medium">{t(`customerPortal.operations.${operation}.label`)}</p>
                <p className="text-muted-foreground text-sm">
                  {t(`customerPortal.operations.${operation}.description`)}
                </p>
              </div>
              <Switch
                id={`customer-portal-${operation}`}
                checked={current[operation]}
                onCheckedChange={(checked) =>
                  setDraft((prev) => ({ ...(prev ?? saved), [operation]: checked }))
                }
              />
            </div>
          ))
        )}
        <p className="text-muted-foreground border-t pt-4 text-sm">
          {t('customerPortal.operations.alwaysOnNote')}
        </p>
      </CardContent>
    </Card>
  );
};

const CustomerPortalSettingsPageInner = () => {
  const { t } = useTranslation('stores');
  const { id: storeId } = useParams<{ id: string }>();

  if (!storeId) return null;

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl font-semibold sm:text-2xl">{t('customerPortal.title')}</h1>
        <p className="text-muted-foreground text-sm">{t('customerPortal.description')}</p>
      </div>

      <PortalMountCard storeId={storeId} />
      <PortalOperationsCard storeId={storeId} />
    </div>
  );
};

export const CustomerPortalSettingsPage = withRequiredPermission(
  CustomerPortalSettingsPageInner,
  PERMISSIONS.STORES_MANAGE,
);
